import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useUploadContactFile } from "../../../hooks/crm/useContacts";
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { FileMetadataDTO, SystemDataType } from "../../../interfaces/DTO/storageFile";
import { formatSystemDataType } from "../../../utils/featuresFormatter";
import { HandlerAutocompleteField } from "../../../shared/Autocompletes/HandlerAutocompleteField";

interface Props {
  contactId: number;
  showUploadArea: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  initialSystemDataType?: SystemDataType;
  initialHandlerCode?: string;
  autoOpen?: boolean;
}

const ContactFileUploader: React.FC<Props> = ({
  contactId,
  showUploadArea,
  onClose,
  onUploadSuccess,
  initialSystemDataType,
  initialHandlerCode,
  autoOpen = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [customName, setCustomName] = useState<string>("");
  const [systemDataType, setSystemDataType] = useState<SystemDataType | "">(initialSystemDataType ?? "");
  const [targetHandlerCode, setTargetHandlerCode] = useState<string>(initialHandlerCode ?? "");
  const [customTag, setCustomTag] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useUploadContactFile(contactId);

  useEffect(() => {
    if (autoOpen) {
      setTimeout(() => fileInputRef.current?.click(), 0);
    }
  }, []);

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setIsUploadDialogOpen(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleCloseDialog = () => {
    setFile(null);
    setIsUploadDialogOpen(false);
    setCustomName("");
    setSystemDataType("");
    setTargetHandlerCode("");
    setCustomTag("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = () => {
    if (!file || !systemDataType) return;

    const metadata: FileMetadataDTO = {
      system_data_type: systemDataType,
      target_handler_code: targetHandlerCode || undefined,
      custom_tag: customTag || undefined,
    };

    uploadFile.mutate({ file, customName, metadata }, {
      onSuccess: () => {
        handleCloseDialog();
        onClose();
        onUploadSuccess();
      }
    });
  };

  const isUploadDisabled = !file || !systemDataType || uploadFile.isPending;

  return (
    <>
      {/* input תמיד קיים ב-DOM כדי לאפשר פתיחה פרוגרמטית */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* איזור גרירה - מוצג רק אם showUploadArea true */}
      {showUploadArea && (
        <Box sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 6,
              textAlign: 'center',
              backgroundColor: isDragging ? 'primary.50' : 'grey.50',
              transition: 'all 0.3s',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.light',
                backgroundColor: 'grey.100',
              }
            }}
          >

            <Box>
              <CloudUploadIcon sx={{ fontSize: 64, color: 'grey.700', mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                גרור ושחרר קובץ כאן
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: 'text.disabled', display: 'block' }}>
                או
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => fileInputRef.current?.click()}
                sx={{ mt: 1 }}
              >
                בחר קובץ
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* דיאלוג למילוי פרטי קובץ */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">פרטי קובץ להעלאה</Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dir="rtl">
          <Box display="flex" flexDirection="column" gap={3} pt={2}>
            {/* שם הקובץ שנבחר */}
            {file && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                backgroundColor: 'success.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.200'
              }}>
                <CloudUploadIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              </Box>
            )}

            {/* סוג נתון - חובה */}
            <FormControl fullWidth required>
              <InputLabel id="system-data-type-label">
                סוג נתון <span style={{ color: 'red' }}>*</span>
              </InputLabel>
              <Select
                labelId="system-data-type-label"
                value={systemDataType}
                onChange={(e) => setSystemDataType(e.target.value as SystemDataType)}
                label="סוג נתון *"
              >
                <MenuItem value="">בחר סוג נתון</MenuItem>
                {Object.values(SystemDataType).map((value) => (
                  <MenuItem key={value} value={value}>
                    {formatSystemDataType(value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* שם מותאם - אופציונלי */}
            <TextField
              label="שם מותאם"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              fullWidth
              helperText="אופציונלי"
            />

            {/* קוד יצרן - אופציונלי */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                קוד יצרן (אופציונלי)
              </Typography>
              <HandlerAutocompleteField
                value={targetHandlerCode}
                onChange={(newValue) => setTargetHandlerCode(newValue || "")}
                placeholder="בחר יצרן..."
              />
            </Box>

            {/* תגית מותאמת - אופציונלי */}
            <TextField
              label="תגית מותאמת"
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              fullWidth
              helperText="אופציונלי"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-start' }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            ביטול
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isUploadDisabled}
            startIcon={uploadFile.isPending ? undefined : <CloudUploadIcon />}
            sx={{
              '& .MuiButton-startIcon': {
                marginRight: 1,
                marginLeft: 0
              }
            }}
          >
            {uploadFile.isPending ? "מעלה..." : "העלה קובץ"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactFileUploader;
